import { useEffect } from "react";

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
  keywords?: string;
  article?: {
    author: string;
    publishedDate: string;
    modifiedDate?: string;
    tags: string[];
    imageUrl: string;
    imageAlt: string;
    category?: string;
    wordCount?: number;
    body?: string;
  };
  /** For /author/:slug pages — name shown in breadcrumb */
  authorName?: string;
  /** For paginated category pages — appends "Page N" breadcrumb */
  pageNumber?: number;
}

const BASE_URL = "https://globalenergyinsider.com";
const DEFAULT_OG_IMAGE = "https://globalenergyinsider.com/og-image.jpg";

const slugifyCategory = (cat: string) =>
  cat.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const PageSEO = ({
  title,
  description,
  canonicalPath = "/",
  ogType = "website",
  ogImage,
  noIndex = false,
  keywords,
  article,
  authorName,
  pageNumber,
}: PageSEOProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);

    if (keywords) {
      setMeta("name", "keywords", keywords);
    } else if (article?.tags) {
      setMeta("name", "keywords", article.tags.join(", "));
    }

    if (noIndex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE_URL}${canonicalPath}`);

    // Open Graph
    const img = ogImage || article?.imageUrl || DEFAULT_OG_IMAGE;
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", `${BASE_URL}${canonicalPath}`);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", img);

    // Twitter
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);

    if (article) {
      setMeta("property", "article:published_time", article.publishedDate);
      if (article.modifiedDate) {
        setMeta("property", "article:modified_time", article.modifiedDate);
      }
      setMeta("property", "article:author", article.author);
      if (article.category) {
        setMeta("property", "article:section", article.category);
      }
      article.tags.forEach((tag) => {
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", tag);
        el.setAttribute("data-page-seo", "true");
        document.head.appendChild(el);
      });
    }

    // ---- NewsArticle JSON-LD ----
    const existingLd = document.querySelector('script[data-page-seo-ld]');
    if (existingLd) existingLd.remove();

    if (article) {
      const headline = title.replace(" | Global Energy Insider", "");
      const articleUrl = `${BASE_URL}${canonicalPath}`;
      const newsArticle: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": headline.length > 110 ? headline.slice(0, 107) + "..." : headline,
        "description": description,
        "image": [img],
        "datePublished": article.publishedDate,
        "dateModified": article.modifiedDate || article.publishedDate,
        "author": [{
          "@type": "Person",
          "name": article.author,
          "url": `${BASE_URL}/editorial-team`,
        }],
        "publisher": {
          "@type": "NewsMediaOrganization",
          "@id": "https://globalenergyinsider.com/#organization",
          "name": "Global Energy Insider",
          "logo": {
            "@type": "ImageObject",
            "url": "https://globalenergyinsider.com/favicon.png",
            "width": 512,
            "height": 512,
          },
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": articleUrl,
        },
        "url": articleUrl,
        "isAccessibleForFree": true,
        "inLanguage": "en-US",
        "keywords": article.tags.join(", "),
      };
      if (article.category) newsArticle.articleSection = article.category;
      if (article.wordCount) newsArticle.wordCount = article.wordCount;

      const ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.setAttribute("data-page-seo-ld", "true");
      ldScript.textContent = JSON.stringify(newsArticle);
      document.head.appendChild(ldScript);
    }

    // ---- BreadcrumbList JSON-LD ----
    const existingBreadcrumb = document.querySelector('script[data-breadcrumb-ld]');
    if (existingBreadcrumb) existingBreadcrumb.remove();

    if (canonicalPath !== "/") {
      const items: Array<Record<string, unknown>> = [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      ];

      if (canonicalPath.startsWith("/article/") && article?.category) {
        // Home → Category → Article
        items.push({
          "@type": "ListItem",
          "position": 2,
          "name": article.category,
          "item": `${BASE_URL}/category/${slugifyCategory(article.category)}`,
        });
        items.push({
          "@type": "ListItem",
          "position": 3,
          "name": title.replace(" | Global Energy Insider", ""),
          "item": `${BASE_URL}${canonicalPath}`,
        });
      } else if (canonicalPath.startsWith("/article/")) {
        items.push({
          "@type": "ListItem",
          "position": 2,
          "name": title.replace(" | Global Energy Insider", ""),
          "item": `${BASE_URL}${canonicalPath}`,
        });
      } else if (canonicalPath.startsWith("/author/") && authorName) {
        // Home → Editorial Team → Author
        items.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Editorial Team",
          "item": `${BASE_URL}/about`,
        });
        items.push({
          "@type": "ListItem",
          "position": 3,
          "name": authorName,
          "item": `${BASE_URL}${canonicalPath}`,
        });
      } else if (canonicalPath.startsWith("/category/")) {
        const basePath = canonicalPath.split("?")[0];
        const catName = basePath.replace("/category/", "").replace(/-/g, " ");
        items.push({
          "@type": "ListItem",
          "position": 2,
          "name": catName.replace(/\b\w/g, (c) => c.toUpperCase()),
          "item": `${BASE_URL}${basePath}`,
        });
        if (pageNumber && pageNumber > 1) {
          items.push({
            "@type": "ListItem",
            "position": 3,
            "name": `Page ${pageNumber}`,
            "item": `${BASE_URL}${basePath}?page=${pageNumber}`,
          });
        }
      } else {
        const pageName = canonicalPath.replace(/^\//, "").replace(/-/g, " ");
        items.push({
          "@type": "ListItem",
          "position": 2,
          "name": pageName.replace(/\b\w/g, (c) => c.toUpperCase()),
          "item": `${BASE_URL}${canonicalPath}`,
        });
      }

      const breadcrumbLd = document.createElement("script");
      breadcrumbLd.type = "application/ld+json";
      breadcrumbLd.setAttribute("data-breadcrumb-ld", "true");
      breadcrumbLd.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items,
      });
      document.head.appendChild(breadcrumbLd);
    }

    return () => {
      document.querySelectorAll('meta[data-page-seo]').forEach((el) => el.remove());
      const ld = document.querySelector('script[data-page-seo-ld]');
      if (ld) ld.remove();
      const bc = document.querySelector('script[data-breadcrumb-ld]');
      if (bc) bc.remove();
    };
  }, [title, description, canonicalPath, ogType, ogImage, noIndex, keywords, article, authorName, pageNumber]);

  return null;
};

export default PageSEO;
